<div class="attachment-library-modal" aria-labelledby="attachment-library-title">
  <header class="attachment-library-modal__header">
    <h2 id="attachment-library-title">{{ 'Attachments.library.title' | translate }}</h2>
    <button type="button" class="attachment-library-modal__close" (click)="close()" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <div class="attachment-library-modal__body">
    <nav class="attachment-library-modal__folders" [attr.aria-label]="'Attachments.library.folders' | translate">
      <button type="button" [class.active]="activeTab() === 'all'" (click)="setActiveTab('all')">
        <mat-icon>folder_open</mat-icon><span>{{ 'Attachments.library.allFiles' | translate }}</span>
      </button>
      @for (group of groupNames; track group) {
        <button type="button" [class.active]="activeTab() === group" (click)="setActiveTab(group)">
          <mat-icon>{{ group === 'photos' ? 'image' : group === 'videos' ? 'videocam' : 'description' }}</mat-icon>
          <span>{{ ('Attachments.library.groups.' + group) | translate }}</span>
        </button>
      }
    </nav>

    <div class="attachment-library-modal__right">
      <div class="attachment-library-modal__filters">
        <label class="attachment-library-modal__search">
          <mat-icon>search</mat-icon>
          <input [formControl]="searchControl" [placeholder]="'Attachments.library.search' | translate" maxlength="255" />
          @if (searchControl.value) {
            <button type="button" (click)="searchControl.setValue('')" [attr.aria-label]="'Attachments.library.clearSearch' | translate">
              <mat-icon>close</mat-icon>
            </button>
          }
        </label>
        <div class="attachment-library-modal__project-selection">
          <project-selection
            [form]="projectForm"
            controlName="projectId"
            [initialValue]="[]"
            [addTag]="false"
            [clearable]="true"
            [placeholder]="'Attachments.library.selectProject' | translate"
          />
        </div>
      </div>

      <div class="attachment-library-modal__toolbar">
        <h3>{{ activeTab() === 'all' ? ('Attachments.library.allFiles' | translate) : (('Attachments.library.groups.' + activeTab()) | translate) }}</h3>
        <div class="attachment-library-modal__toolbar-actions">
          @if (selectedCount) {
            <div class="attachment-library-modal__selection-summary" aria-live="polite">
              <div class="attachment-library-modal__selection-info">
                <mat-icon>check_circle</mat-icon>
                <span>{{ 'Attachments.library.selectedItems' | translate: { count: selectedCount } }}</span>
              </div>
              <button
                type="button"
                class="attachment-library-modal__clear-selection"
                (click)="clearSelection()"
                [attr.aria-label]="'Attachments.library.clearSelection' | translate"
              >
                <mat-icon>close</mat-icon>
              </button>
            </div>
          }
          <div class="attachment-library-modal__view-switch" role="group" aria-label="View mode">
            <button type="button" [class.active]="viewMode() === 'grid'" (click)="setViewMode('grid')"><mat-icon>grid_view</mat-icon></button>
            <button type="button" [class.active]="viewMode() === 'list'" (click)="setViewMode('list')"><mat-icon>view_list</mat-icon></button>
          </div>
        </div>
      </div>

      <div class="attachment-library-modal__file-list" (scroll)="onContentScroll($event)">
      @if (initialLoading()) {
        <div class="attachment-library-modal__loading"><mat-spinner diameter="36" /></div>
      } @else {
        @if (activeTab() === 'all') {
          @if (!getAllFiles().length) {
            <div class="attachment-library-modal__empty">
              <mat-icon>search_off</mat-icon>
              <span>{{ 'Attachments.library.empty' | translate }}</span>
            </div>
          } @else {
            <div class="attachment-library-modal__files" [class.attachment-library-modal__files--list]="viewMode() === 'list'">
              @for (entry of getAllFiles(); track trackItem($index, entry.item)) {
                <ng-container [ngTemplateOutlet]="file" [ngTemplateOutletContext]="{ $implicit: entry.item, group: entry.group }" />
              }
            </div>
          }
        } @else {
          <ng-container [ngTemplateOutlet]="files" [ngTemplateOutletContext]="{ $implicit: activeTab() }" />
        }
      }
      </div>
    </div>
  </div>

  <div class="attachment-library-modal__footer sticky-footer">
    <div>
      <app-button [type]="'button'" (onClick)="close()" [config]="{ size: ButtonSize.LARGE, type: ButtonTypes.NEW_SECONDARY_BLUE, text: 'Attachments.library.cancel' | translate }" />
      <app-button [type]="'button'" (onClick)="submit()" [disabled]="!selectedCount" [config]="{ size: ButtonSize.LARGE, type: ButtonTypes.NEW_PRIMARY_BLUE, text: ('Attachments.library.addSelected' | translate:{ count: selectedCount }) }" />
    </div>
  </div>
</div>

<ng-template #files let-group>
  @if (!getGroup(group).records.length && !getGroup(group).loading) {
    <div class="attachment-library-modal__empty">
      <mat-icon>search_off</mat-icon>
      <span>{{ 'Attachments.library.empty' | translate }}</span>
    </div>
  } @else {
    <div class="attachment-library-modal__files" [class.attachment-library-modal__files--list]="viewMode() === 'list'">
      @for (item of getGroup(group).records; track trackItem($index, item)) {
        <ng-container [ngTemplateOutlet]="file" [ngTemplateOutletContext]="{ $implicit: item, group: group }" />
      }
    </div>
    @if (getGroup(group).loading) { <div class="attachment-library-modal__loading"><mat-spinner diameter="28" /></div> }
  }
</ng-template>

<ng-template #file let-item let-group="group">
  <div class="attachment-library-modal__file" [class.selected]="isSelected(item)" [class.disabled]="!isSelectable(item, group)">
          <button type="button" class="attachment-library-modal__file-content" (click)="preview(item)">
          <span class="attachment-library-modal__preview">
            @if (item.isImage || (item.isVideo && item.hasPoster)) {
              <img [src]="thumbnailUrl(item)" [alt]="item.name" />
              @if (item.isVideo) { <mat-icon class="attachment-library-modal__video-play">play_arrow</mat-icon> }
            } @else {
              <mat-icon>{{ item.isVideo ? 'play_circle' : 'description' }}</mat-icon>
            }
          </span>
          <span class="attachment-library-modal__file-data">
            <strong [matTooltip]="item.name" matTooltipPosition="above" matTooltipShowDelay="1000">{{ item.name }}</strong>
            @if (sourceLabel(item); as source) { <em>{{ source }}</em> }
          </span>
          </button>
          <button
            type="button"
            class="attachment-library-modal__selection-toggle"
            [class.attachment-library-modal__selection-toggle--selected]="isSelected(item)"
            [disabled]="!isSelectable(item, group)"
            [attr.aria-pressed]="isSelected(item)"
            (click)="toggleSelection(item, group, $event)"
          ><mat-icon>check</mat-icon></button>
        </div>
</ng-template>
